import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  computed,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { APP } from 'src/app/config/app';
import { ICustomToggleOptions } from 'src/app/models';
import { CustomToggleTone } from 'src/app/models/enums';
import { LiteralService } from 'src/app/services';
import { TooltipModule } from 'src/app/shared/tooltip/tooltip.module';

@Component({
  selector: 'app-custom-toggle',
  templateUrl: './custom-toggle.component.html',
  styleUrls: ['./custom-toggle.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TooltipModule],
})
export class CustomToggleComponent {
  readonly id = input<string>();
  readonly options = input.required<ICustomToggleOptions>();
  readonly selectedValue = input<unknown>();
  readonly small = input(false);
  readonly controlled = input(false);
  readonly action = output<unknown>();

  private readonly tumbler = viewChild.required<ElementRef<HTMLElement>>('tumbler');
  private readonly thumbToneClasses: Record<CustomToggleTone, string> = {
    [CustomToggleTone.PRIMARY]: 'tumbler-primary',
    [CustomToggleTone.SECONDARY]: 'tumbler-secondary',
    [CustomToggleTone.DANGER]: 'tumbler-danger',
    [CustomToggleTone.SUCCESS]: 'tumbler-success',
  };
  private readonly iconToneClasses: Record<CustomToggleTone, string> = {
    [CustomToggleTone.PRIMARY]: 'filter-primary-purple-light',
    [CustomToggleTone.SECONDARY]: 'filter-primary-purple-light',
    [CustomToggleTone.DANGER]: 'filter-secondary-red',
    [CustomToggleTone.SUCCESS]: 'filter-secondary-green-dark',
  };
  private readonly minOffset = -2;
  private dragging = false;
  private dragMoved = false;
  private dragStartX = 0;

  readonly secondOption = linkedSignal(() => this.selectedValue() === this.options()[1].value);
  readonly showIcons = signal(true);
  readonly dragOffset = signal<number | null>(null);
  readonly selectedOption = computed(() => this.options()[this.secondOption() ? 1 : 0]);
  readonly otherOption = computed(() => this.options()[this.secondOption() ? 0 : 1]);
  private readonly selectedTone = computed(() => this.selectedOption().tone ?? CustomToggleTone.PRIMARY);
  readonly thumbClasses = computed(() => {
    const size = this.small() ? 'w-8 h-8 -top-1' : 'w-10 h-10 -top-0.5';
    return `${size} ${this.thumbToneClasses[this.selectedTone()]}`;
  });
  readonly selectedIconClass = computed(() => this.iconToneClasses[this.selectedTone()]);
  readonly thumbTransform = computed(() => {
    const offset = this.dragOffset();
    if (offset !== null) return `translateX(${offset}px)`;
    return this.secondOption() ? 'translateX(85%)' : 'translateX(calc(0% - 2px))';
  });

  constructor(public literalService: LiteralService) {}

  changeMode() {
    if (this.controlled()) {
      this.action.emit(this.otherOption().value);
      return;
    }
    this.showIcons.set(false);
    this.secondOption.update((secondOption) => !secondOption);
    setTimeout(() => this.showIcons.set(true), APP.CUSTOM_TOGGLE_ICON_FADE_MS);
    this.action.emit(this.selectedOption().value);
  }

  onToggleKey(event: Event) {
    event.preventDefault();
    this.changeMode();
  }

  onPointerDown(event: PointerEvent) {
    this.dragging = true;
    this.dragMoved = false;
    this.dragStartX = event.clientX;
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
  }

  onPointerMove(event: PointerEvent) {
    if (!this.dragging) return;
    const delta = event.clientX - this.dragStartX;
    if (Math.abs(delta) > APP.CUSTOM_TOGGLE_DRAG_THRESHOLD_PX) this.dragMoved = true;
    const start = this.secondOption() ? this.maxOffset() : this.minOffset;
    this.dragOffset.set(Math.min(Math.max(start + delta, this.minOffset), this.maxOffset()));
  }

  onPointerUp() {
    if (!this.dragging) return;
    this.dragging = false;
    const offset = this.dragOffset();
    this.dragOffset.set(null);
    if (!this.dragMoved || offset === null) {
      this.changeMode();
      return;
    }
    if (offset > this.maxOffset() / 2 !== this.secondOption()) {
      this.changeMode();
    }
  }

  onPointerCancel() {
    this.dragging = false;
    this.dragOffset.set(null);
  }

  private maxOffset(): number {
    return this.tumbler().nativeElement.offsetWidth * 0.85;
  }
}
